// @ts-nocheck
import Link, { LinkProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import applyPropBuilder from "@insite/mobius/utilities/applyPropBuilder";
import { StyledProp } from "@insite/mobius/utilities/InjectableCss";
import injectCss from "@insite/mobius/utilities/injectCss";
import MobiusStyledComponentProps from "@insite/mobius/utilities/MobiusStyledComponentProps";
import * as React from "react";
import styled, { withTheme } from "styled-components";

/* Starter Custom Code - Start */
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import constants from "@store/constants";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
/* Starter Custom Code - End */

/* Store Custom Code - Start */
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import ArrowRight from "@insite/mobius/Icons/ArrowRight";
import { iconScalableStyles } from "@toolkit/Themes/common/mixins";
import getColor from "@insite/mobius/utilities/getColor";
/* Store Custom Code - End */

export type BreadcrumbsProps = MobiusStyledComponentProps<
    "nav",
    {
        /** CSS string or styled-components function to be injected into this component.
         * @themable */
        css?: StyledProp<BreadcrumbsProps>;
        /** An array of link props */
        links?: LinkProps[];
        /**
         * Indicates how the `css` property is combined with the variant `css` property from the theme.
         * If true, the variant css is applied first and then the component css is applied after causing
         * a merge, much like normal CSS. If false, only the component css is applied, overriding the variant css in the theme.
         */
        mergeCss?: boolean;
        /**
         * Optional props to be passed down to all Link and Typography components.
         * Can be overriden by individual typographyProps in the links array.
         * @themable */
        typographyProps?: TypographyPresentationProps;
    }
>;

export type BreadcrumbsPresentationProps = Omit<BreadcrumbsProps, "links">;

/* Store Custom Code - Start */
export const IconArrowRight = styled(Icon).attrs<IconPresentationProps>(() => ({
    src: ArrowRight,
}))<IconPresentationProps>`
    ${iconScalableStyles("1.25em")}
    margin: 0 0.75em;
`;
/* Store Custom Code - End */

export const Slash = styled(Typography as any).attrs({ children: "/" })`
    margin: 0 ${pxToRem(10)}; /* Starter Custom Code */
    /* Starter Removed - display */
`;

export const StyledNav = styled.nav.attrs({ "aria-label": "breadcrumbs" })`
    /* Starter Custom Code - Start */
    ol {
        display: flex;
        flex-direction: row;
        flex-wrap: wrap;
        align-items: center;
        padding: 0;
        margin: 0;
        list-style: none;
        /* Store Custom Code - Start */
        padding-bottom: ${pxToRem(16)};
        border-bottom: 1px solid ${getColor("common.disabledDark")};

        @media print {
            display: none;
        }
        /* Store Custom Code - End */
        /* Store Moved - injectCss - down */
    }
    /* Starter Custom Code - End */
    /* Store Custom Code - Start */
    li {
        display: flex;
    }
    ${injectCss}/* Store Custom Code - End */
`;

const Breadcrumbs: React.FC<BreadcrumbsProps> = withTheme(({ links, mergeCss, ...otherProps }) => {
    const { spreadProps, applyStyledProp } = applyPropBuilder(otherProps, { component: "breadcrumbs" });
    const typographyProps = spreadProps("typographyProps");
    const resolvedMergeCss = mergeCss ?? otherProps?.theme?.breadcrumbs?.defaultProps?.mergeCss;
    // return null if `links` is an empty empty object, collection, map, or set.
    if (!links || links.length === 0) {
        return null;
    }
    const children = links.map((link: any, index: number) => {
        const children = link.children;
        // yield null if no children
        if (!React.Children.count(children)) {
            return null;
        }
        const key = typeof children === "string" ? `${children}_${index}` : index;
        /* Starter Custom Code - Start */
        const isCurrent = index === links.length - 1;
        const isInaccessible = !link.href || link.type === constants.pageType.folder;

        return (
            <StyledWrapper as="li" key={key}>
                {isCurrent || isInaccessible ? (
                    <Typography
                        mergeCss={resolvedMergeCss}
                        {...typographyProps}
                        aria-current={isCurrent ? "page" : undefined}
                        data-test-selector={`breadCrumb_${children}`}
                    >
                        {children}
                    </Typography>
                ) : (
                    <Link typographyProps={typographyProps} {...link} href={isInaccessible ? undefined : link.href} />
                )}
                {!isCurrent && <IconArrowRight />} {/* Store Custom Code */}
            </StyledWrapper>
        );
        /* Starter Custom Code - End */
    });
    // return null if `links` map yields an array of nulls
    if (Object.keys(children.filter((x: typeof Link) => x !== null)).length === 0) {
        return null;
    }

    return (
        <StyledNav {...otherProps} css={applyStyledProp("css", resolvedMergeCss)}>
            <ol>{children}</ol> {/* Starter Custom Code */}
        </StyledNav>
    );
});

Breadcrumbs.displayName = "Breadcrumbs";
/** @component */
export default Breadcrumbs;
